/* eslint-disable object-curly-newline */
/* eslint-disable max-len */
import React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Drawer from "@mui/material/Drawer";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import ListItemAvatar from "@mui/material/ListItemAvatar";

import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import SompoLoading from "../loading/SompoLoading";
import SompoTextField from "../textField/SompoTextField";
import type { HeaderProps } from "./Header";
import Category from "../category/Category";

import SompoContext from "../../logic/context/SompoContext";
import { showSearchMenu, showSkillMenu, showInformationMenu, showUserMenu } from "../../logic/companies/Companies";

import { ZINDEX_MENU } from "../Theme";

const MenuDrawer = styled(Drawer)(() => ({
    "& .MuiDrawer-paper": {
        width: "80% !important",
    },
}));

const SearchDrawer = styled(Drawer)(() => ({
    "& .MuiDrawer-paper": {
        height: "100% !important",
    },
}));

const Mobile = (props: HeaderProps) => {
    const sompoContext = React.useContext(SompoContext);

    // メニュードロワー用
    const [menuOpen, setMenuOpen] = React.useState(false);

    // メニュードロワーの開閉
    const menuDrawer = (newOpen: boolean) => () => {
        setMenuOpen(newOpen);
    };

    // 検索のドロワー用
    const searchText = React.useRef<HTMLInputElement>(null!);
    const [searchDrawerOpen, setSearchDrawerOpen] = React.useState(false);

    // 検索のドロワーを開く
    const openSearchDrawer = () => {
        setSearchDrawerOpen(true);
    };

    // 検索のドロワーを閉じる
    const closeSearchDrawer = () => {
        setSearchDrawerOpen(false);
    };

    const handleInputChange = (event: { target: { value: string } }) => {
        props.setTrainingSearchText(event.target.value);
    };

    const search = () => {
        closeSearchDrawer();
        props.handleTrainingList();
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center" columnSpacing={2} sx={{ px: 2 }}>
                <Grid
                    item
                    xs={2}
                    sx={{
                        "&: hover": {
                            cursor: "pointer",
                        },
                    }}
                >
                    <MenuIcon onClick={menuDrawer(true)} />
                </Grid>

                <Grid
                    item
                    sx={{
                        "&: hover": {
                            cursor: "pointer",
                        },
                    }}
                    onClick={props.handleHome}
                >
                    {(() => {
                        const image = sompoContext.companySettings?.ui?.title;
                        if (!image) {
                            return <></>;
                        }
                        return <Box component="img" src={image} sx={{ height: "32px", maxHeight: "32px" }} />;
                    })()}
                </Grid>

                <Grid item xs={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box sx={{ mr: 1 }}>
                        {showInformationMenu() && (
                            <Tooltip title="お知らせ">
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        transition: ".3s",
                                        "&: hover": {
                                            cursor: "pointer",
                                        },
                                    }}
                                    onClick={props.handleInfo}
                                >
                                    {props.hasNewInformation ? (
                                        <Badge
                                            sx={{
                                                "& .MuiBadge-badge": {
                                                    backgroundColor: "#C00000",
                                                },
                                            }}
                                            badgeContent={""}
                                            color="info"
                                            variant="dot"
                                            overlap="circular"
                                        >
                                            <NotificationsIcon />
                                        </Badge>
                                    ) : (
                                        <NotificationsIcon />
                                    )}
                                </Box>
                            </Tooltip>
                        )}
                    </Box>

                    {showSearchMenu() && (
                        <SearchIcon
                            sx={{
                                fontSize: "1.6rem",
                                "&: hover": {
                                    cursor: "pointer",
                                },
                            }}
                            onClick={openSearchDrawer}
                        />
                    )}
                </Grid>
            </Grid>

            <MenuDrawer open={menuOpen} onClose={menuDrawer(false)} sx={{ zIndex: ZINDEX_MENU, width: "100%" }}>
                <IconButton onClick={menuDrawer(false)} sx={{ position: "fixed", right: "30px", top: "30px" }}>
                    <CloseIcon sx={{ color: "#fefefe", fontSize: "1.5em" }} />
                </IconButton>
                <Box sx={{ width: "100%", mt: "64px" }} role="presentation" onClick={menuDrawer(false)}>
                    <List>
                        <ListItemButton sx={{ p: 2 }}>
                            <ListItemAvatar>
                                {(() => {
                                    const image = sompoContext.userImage;
                                    if (!image) {
                                        return <AccountCircleIcon />;
                                    }
                                    return <Avatar src={image} />;
                                })()}
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                                        {sompoContext.userDetail.familyNameJa} {sompoContext.userDetail.firstNameJa}
                                    </Typography>
                                }
                                secondary={<Typography variant="body2">{sompoContext.userDetail.email1}</Typography>}
                            />
                        </ListItemButton>
                        <Divider />

                        {showInformationMenu() && (
                            <ListItemButton sx={{ p: 2 }} onClick={props.handleInfo}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                            お知らせ一覧
                                        </Typography>
                                    }
                                />
                                <KeyboardArrowRightIcon />
                            </ListItemButton>
                        )}

                        {showUserMenu() && (
                            <ListItemButton sx={{ p: 2 }} onClick={props.handleProfile}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                            マイページ・受講履歴
                                        </Typography>
                                    }
                                />
                                <KeyboardArrowRightIcon />
                            </ListItemButton>
                        )}
                        {sompoContext.loading || sompoContext.skillMapLoading || sompoContext.skillMapDrawerLoading ? (
                            <ListItemButton sx={{ p: 2 }} onClick={props.handleProfile}>
                                <SompoLoading />
                            </ListItemButton>
                        ) : (
                            <ListItemButton sx={{ p: 2 }} onClick={props.handleLogout}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                            ログアウト
                                        </Typography>
                                    }
                                />
                                <KeyboardArrowRightIcon />
                            </ListItemButton>
                        )}
                    </List>
                </Box>
            </MenuDrawer>

            <Box>
                <SearchDrawer
                    anchor="top"
                    open={searchDrawerOpen}
                    onClose={closeSearchDrawer}
                    disableAutoFocus // ドロワ側への自動フォーカスを無効化
                    disableEnforceFocus // ドロワからフォーカスが外せるように
                    ModalProps={{ disableScrollLock: true }}
                    sx={{ zIndex: ZINDEX_MENU }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            flexFlow: "column",
                        }}
                    >
                        <Box sx={{ width: "100%", pt: 2, pb: 2, textAlign: "right" }}>
                            <IconButton
                                onClick={(event: React.MouseEvent<unknown>) => {
                                    closeSearchDrawer();
                                    event.stopPropagation();
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ width: "90%", pt: 2, pb: 5 }}>
                            <form
                                onSubmit={search}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <SompoTextField
                                    size="small"
                                    variant="outlined"
                                    label={
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-start",
                                                alignItems: "center",
                                            }}
                                        >
                                            <SearchIcon sx={{ fontSize: "1.2rem" }} />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    color: "#7C8188",
                                                }}
                                            >
                                                研修・講座を検索
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        width: "90%",
                                        borderRadius: "10px",
                                        backgroundColor: "#F5F6F6",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderRadius: "10px",
                                        },
                                    }}
                                    onClick={openSearchDrawer}
                                    onChange={handleInputChange}
                                    ref={searchText}
                                />
                            </form>
                        </Box>

                        {showSkillMenu() && (
                            <Box sx={{ width: "100%" }}>
                                <ListItemButton sx={{ p: 2, color: "#C11000", borderTop: "7px solid #f5f6f6" }} onClick={props.handleSkillMap}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                スキルから探す
                                            </Typography>
                                        }
                                    />
                                    <KeyboardArrowRightIcon />
                                </ListItemButton>
                            </Box>
                        )}

                        <Category setDrawerOpen={setSearchDrawerOpen} />
                    </Box>
                </SearchDrawer>
            </Box>
        </Box>
    );
};
export default Mobile;
